<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const inboxes = ref([]);
const agentsByInbox = ref({});
const loading = ref(true);
const savingInboxId = ref(null);

const voiceInboxes = computed(() =>
  inboxes.value.filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp' && inbox.voice_enabled
  )
);

const strategyLabel = strategy =>
  strategy === 'ring_all' ? 'Tocar para todos' : 'Responsável primeiro';

const load = async () => {
  loading.value = true;
  try {
    const { data } = await InboxesAPI.get();
    inboxes.value = data.payload || [];

    await Promise.all(
      voiceInboxes.value.map(async inbox => {
        try {
          const response = await InboxesAPI.getAssignableAgents(inbox.id);
          agentsByInbox.value[inbox.id] = response.data.payload || [];
        } catch {
          agentsByInbox.value[inbox.id] = [];
        }
      })
    );
  } catch {
    useAlert('Não foi possível carregar as filas de chamadas.');
  } finally {
    loading.value = false;
  }
};

const toggleAgent = (inbox, agentId) => {
  const config = inbox.call_queue_config || {};
  const current = [...(config.agent_ids || [])];
  const id = Number(agentId);
  const next = current.includes(id)
    ? current.filter(value => value !== id)
    : [...current, id];
  inbox.call_queue_config = { ...config, agent_ids: next };
};

const save = async inbox => {
  savingInboxId.value = inbox.id;
  try {
    const config = inbox.call_queue_config || {};
    await InboxesAPI.setCallQueue(inbox.id, {
      strategy: config.strategy || 'assigned_then_fallback',
      agent_ids: (config.agent_ids || []).map(Number),
    });
    useAlert('Fila de chamadas salva.');
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Não foi possível salvar a fila.'
    );
  } finally {
    savingInboxId.value = null;
  }
};

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="flex-1 min-w-0 overflow-y-auto bg-n-background">
    <div class="w-full px-6 py-8 2xl:px-10">
      <header class="flex items-start gap-4 mb-8">
        <div
          class="grid place-items-center size-12 rounded-xl bg-n-brand/10 text-n-brand"
        >
          <i class="i-lucide-phone-call text-2xl" />
        </div>
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            Configurações de ligações
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Defina como os alertas de chamadas do WhatsApp são distribuídos em
            cada caixa.
          </p>
        </div>
      </header>

      <section
        class="mb-7 rounded-xl border border-n-weak bg-n-surface-1 p-5 flex gap-4"
      >
        <i class="i-lucide-git-branch text-xl text-n-brand mt-0.5" />
        <div>
          <h2 class="font-medium text-n-slate-12">
            Chamadas simultâneas e distribuição por fila
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            Cada chamada recebida é tratada separadamente. A tela configura os
            agentes que recebem o alerta; a capacidade efetiva também depende da
            Meta, da rede e dos dispositivos dos atendentes.
          </p>
          <p class="mt-2 text-xs text-n-slate-10">
            Esta versão distribui alertas entre agentes; não oferece espera em
            fila, música, URA ou transbordo temporizado de PABX.
          </p>
        </div>
      </section>

      <div v-if="loading" class="grid place-items-center min-h-48">
        <Spinner />
      </div>

      <section v-else-if="voiceInboxes.length" class="grid grid-cols-1 gap-5">
        <article
          v-for="inbox in voiceInboxes"
          :key="inbox.id"
          class="rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden"
        >
          <div
            class="flex items-start justify-between gap-4 p-5 border-b border-n-weak"
          >
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <i class="i-lucide-phone text-n-brand" />
                <h2 class="font-semibold text-n-slate-12 truncate">
                  {{ inbox.name }}
                </h2>
              </div>
              <p class="mt-1 text-xs text-n-slate-10">Caixa #{{ inbox.id }}</p>
            </div>
            <span
              class="shrink-0 rounded-full px-2.5 py-1 text-xs"
              :class="
                inbox.inbound_calls_enabled
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-amber-3 text-n-amber-11'
              "
            >
              {{
                inbox.inbound_calls_enabled
                  ? 'Recebimento ativo'
                  : 'Recebimento desativado'
              }}
            </span>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 2xl:p-8">
            <fieldset>
              <legend class="text-sm font-semibold text-n-slate-12 mb-3">
                Como distribuir
              </legend>
              <label
                class="flex items-start gap-3 p-4 rounded-xl border border-n-weak mb-3 cursor-pointer transition-colors hover:bg-n-alpha-2 has-[:checked]:border-n-brand/50 has-[:checked]:bg-n-brand/5"
              >
                <input
                  v-model="inbox.call_queue_config.strategy"
                  type="radio"
                  value="assigned_then_fallback"
                  class="mt-1"
                />
                <span>
                  <span class="block text-sm font-medium text-n-slate-12"
                    >Responsável primeiro</span
                  >
                  <span class="block text-xs text-n-slate-11 mt-1"
                    >Toca para o responsável online; se ele estiver offline,
                    chama a equipe elegível.</span
                  >
                </span>
              </label>
              <label
                class="flex items-start gap-3 p-4 rounded-xl border border-n-weak cursor-pointer transition-colors hover:bg-n-alpha-2 has-[:checked]:border-n-brand/50 has-[:checked]:bg-n-brand/5"
              >
                <input
                  v-model="inbox.call_queue_config.strategy"
                  type="radio"
                  value="ring_all"
                  class="mt-1"
                />
                <span>
                  <span class="block text-sm font-medium text-n-slate-12"
                    >Tocar para todos</span
                  >
                  <span class="block text-xs text-n-slate-11 mt-1"
                    >Envia o alerta ao mesmo tempo para todos os agentes online
                    selecionados.</span
                  >
                </span>
              </label>
            </fieldset>

            <fieldset>
              <legend class="text-sm font-semibold text-n-slate-12">
                Agentes da fila
              </legend>
              <p class="text-xs text-n-slate-11 mt-1 mb-3">
                {{
                  inbox.call_queue_config.agent_ids?.length
                    ? 'Somente os agentes marcados recebem a chamada.'
                    : 'Sem seleção, todos os agentes vinculados à caixa são elegíveis.'
                }}
              </p>
              <div
                v-if="agentsByInbox[inbox.id]?.length"
                class="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1"
              >
                <label
                  v-for="agent in agentsByInbox[inbox.id]"
                  :key="agent.id"
                  class="flex min-w-0 items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-3 cursor-pointer transition-colors hover:bg-n-alpha-2"
                >
                  <input
                    type="checkbox"
                    :checked="
                      inbox.call_queue_config.agent_ids?.includes(agent.id)
                    "
                    @change="toggleAgent(inbox, agent.id)"
                  />
                  <span
                    class="min-w-0 flex-1 truncate text-sm text-n-slate-12"
                    >{{ agent.name }}</span
                  >
                </label>
              </div>
              <p
                v-else
                class="text-sm text-n-slate-10 rounded-lg border border-dashed border-n-weak p-4"
              >
                Nenhum agente disponível nesta caixa. Adicione agentes nas
                configurações da caixa de entrada.
              </p>
            </fieldset>
          </div>

          <footer
            class="flex flex-wrap items-center justify-between gap-4 border-t border-n-weak bg-n-alpha-1 px-6 py-4 2xl:px-8"
          >
            <span class="text-sm text-n-slate-10">
              Estratégia ativa:
              <strong class="font-medium text-n-slate-12">{{
                strategyLabel(inbox.call_queue_config.strategy)
              }}</strong>
            </span>
            <Button
              label="Salvar fila"
              icon="i-lucide-save"
              :is-loading="savingInboxId === inbox.id"
              :disabled="savingInboxId === inbox.id"
              @click="save(inbox)"
            />
          </footer>
        </article>
      </section>

      <section
        v-else
        class="rounded-xl border border-dashed border-n-weak bg-n-surface-1 p-10 text-center"
      >
        <i class="i-lucide-phone-off text-3xl text-n-slate-9" />
        <h2 class="mt-3 font-medium text-n-slate-12">
          Nenhuma fila de voz disponível
        </h2>
        <p class="mt-1 text-sm text-n-slate-11 max-w-xl mx-auto">
          Ative as chamadas do WhatsApp e o recebimento de chamadas nas
          configurações de uma caixa. Ela aparecerá aqui para você definir a
          equipe.
        </p>
      </section>
    </div>
  </main>
</template>
